import cn from 'classnames'
import type { CSSProperties } from 'react'
import { type ITooltip, Tooltip } from 'react-tooltip-v6'

type LegacyTooltipProps = Omit<ITooltip, 'render' | 'variant'> & {
  'aria-haspopup'?: string
  backgroundColor?: string
  class?: string
  disable?: boolean
  effect?: string
  event?: string
  getContent?: () => any
  html?: boolean
  isCapture?: boolean
  multiline?: boolean
  padding?: string
  textColor?: string
  type?: ITooltip['variant']
}

const MULTILINE_SEPARATOR = /<br\s*\/?>/

const LegacyTooltipComponent = ({
  backgroundColor,
  children,
  class: legacyClass,
  className,
  disable,
  event,
  getContent,
  html,
  id,
  multiline,
  padding,
  textColor,
  type,
  ...props
}: LegacyTooltipProps) => (
  <Tooltip
    {...props}
    hidden={disable || props.hidden}
    id={id}
    // v4 positioned tooltips as `fixed`, escaping `overflow: hidden` ancestors (cards, tables, carousels)
    positionStrategy={props.positionStrategy ?? 'fixed'}
    // v6's base CSS is imported into `@layer vendor` (src/scss/vendor.css) instead of injected unlayered
    disableStyleInjection
    anchorSelect={props.anchorSelect ?? (id ? `[data-for="${CSS.escape(id)}"]` : undefined)}
    // v4 applied padding/colours through a class-level rule, so theme and component classes could override them.
    // Inline values would beat every stylesheet; they are passed as custom properties consumed in
    // src/scss/components/_tooltip.scss (`@layer app`) instead.
    className={cn(className ?? legacyClass, {
      'react-tooltip-legacy-padding': padding,
      'react-tooltip-legacy-background': backgroundColor,
      'react-tooltip-legacy-text': textColor
    })}
    variant={type}
    openOnClick={event === 'click'}
    style={
      {
        ...(props.style ?? {}),
        ...(padding ? { '--rt-legacy-padding': padding } : {}),
        ...(backgroundColor ? { '--rt-legacy-background': backgroundColor } : {}),
        ...(textColor ? { '--rt-legacy-text': textColor } : {})
      } as CSSProperties
    }
    render={({ activeAnchor, content }) => {
      // v4 content precedence: children, then getContent, then the anchor's data-tip
      const legacyContent = children ?? getContent?.() ?? activeAnchor?.getAttribute('data-tip') ?? content
      if (typeof legacyContent !== 'string') {
        return legacyContent
      }
      if (html) {
        // biome-ignore lint/security/noDangerouslySetInnerHtml: compatibility with react-tooltip v4's explicit html mode
        return <div className='react-tooltip-legacy-html' dangerouslySetInnerHTML={{ __html: legacyContent }} />
      }
      if (multiline && MULTILINE_SEPARATOR.test(legacyContent)) {
        return legacyContent.split(MULTILINE_SEPARATOR).map((line, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static lines of one string, never reordered
          <span key={index} className='react-tooltip-multi-line'>
            {line}
          </span>
        ))
      }
      return legacyContent
    }}
  />
)

const ReactTooltip = Object.assign(LegacyTooltipComponent, {
  hide: () => {},
  rebuild: () => {},
  show: () => {}
})

export default ReactTooltip
export * from 'react-tooltip-v6'
